import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';

import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { TipoContaEnum } from '../../../../../../shared/enums/financas/tipo-conta.enum';
import { CodigoSefazEnum } from '@shared/enums/financas/codigo-sefaz.enum';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { CondicaoPagamento } from '../../../../../../shared/services/models/financas/condicao-pagamento.model';
import { ContaFinanceira } from '../../../../../../shared/services/models/financas/conta-financeira.model';
import { FormaRecebimentoListaStruct } from '../../../../../../shared/services/structs/financas/forma-recebimento-lista.struct';
import { IntermediadorBandeiraSelectStruct } from '../../../../../../shared/services/structs/financas/intermediador-bandeira-select.struct';
import { IntermediadorCondicaoComercialSelectStruct } from '../../../../../../shared/services/structs/financas/intermediador-condicao-comercial-select.struct';
import { IntermediadorPagamentoListaStruct } from '../../../../../../shared/services/structs/financas/intermediador-pagamento-lista.struct';
import { ConfigFinancasStruct } from '../../../../../../shared/services/structs/vendas/config-financas.struct';
import { LookupItemStruct } from '../../../../../../shared/services/structs/vendas/lookup-item.struct';
import { UtilService } from '../../../../../../shared/services/util.service';
import { montarFormParcela } from '../venda-pagamento.form';
import {
    diasDaCondicao,
    diasDoParcelamentoDoIntermediador,
    gerarParcelas,
    interpretarCondicaoLivre,
} from '../venda-pagamento.parcelas';
import { PerfilDaForma, notaDoMecanismo, perfilDaForma } from '../venda-pagamento.perfil';
import {
    ParcelaIntermediadorCalculada,
    calcularParcelasIntermediador,
    dataTarifaSeparada,
    maximoDeParcelas,
    resolverCondicao,
} from '../venda-pagamento.intermediador';
import { TipoTarifacaoEnum } from '../../../../../../shared/enums/financas/tipo-tarifacao.enum';
import { recebimentoViraMovimentacao } from '../venda-pagamento.validacao';
import { VdParcelasComponent } from '../vd-parcelas/vd-parcelas.component';

/** "Meio" do Pix e do Boleto (§8.3): estado de UI, derivável do dado gravado. */
type Meio = 'banco' | 'intermediador';

/** A opção do select da operadora: "3x" com a taxa, quando houver (§8.5, exceção). */
interface OpcaoDaOperadora {
    id: string;
    descricao: string;
    parcelas: number;
    taxa: number | null;
}

/**
 * Prévia do que o lançamento vai gravar (T07). Pelo intermediador: por parcela o vencimento pelo prazo, a taxa, a
 * tarifa rateada e o líquido, mais a tarifa lançada separado; pela condição cadastrada: os juros por parcela.
 */
interface SimulacaoDoRecebimento {
    daOperadora: boolean;
    taxa: number;
    nomeCondicao: string | null;
    parcelas: ParcelaIntermediadorCalculada[];
    totalTaxa: number;
    totalTarifa: number;
    totalLiquido: number;
    tarifaSeparada: { valor: number; data: Date; conta: string } | null;
    totalJuros: number;
    totalComJuros: number;
}

/**
 * UM recebimento do documento (§8.0): a forma, o valor e todo o contexto que a forma traz —
 * conta, intermediador, bandeira, categoria, condição e parcelas.
 *
 * Que campos aparecem sai de `perfilDaForma`, pela NATUREZA da forma (código SEFAZ + tipo de
 * destino), nunca pelo nome — a empresa renomeia formas, e a regra não pode quebrar com isso.
 *
 * O "Meio" (Banco × Intermediador) do Pix e do Boleto não é controle do form: no dado gravado ele
 * é derivável (`idIntermediadorPagamento` preenchido = Intermediador). Aqui é estado de UI que
 * nasce do dado ao abrir e move os campos enquanto o usuário edita.
 */
@Component({
    selector: 'app-vd-recebimento',
    standalone: true,
    imports: [
        AbntPipe,
        DatePipe,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        SelectPesquisavelComponent,
        VdParcelasComponent,
    ],
    templateUrl: './vd-recebimento.component.html',
    styleUrl: './vd-recebimento.component.css',
})
export class VdRecebimentoComponent implements OnInit, OnChanges {
    @Input({ required: true }) public recebimento!: FormGroup;
    @Input() public indice: number = 0;
    @Input() public total: number = 1;

    /** O bloco `financas` do agregado. Nulo enquanto não chegou — os getters devolvem listas vazias. */
    @Input() public financas: ConfigFinancasStruct | null = null;

    /** Configurações ▸ Vendas ▸ Geral ▸ "Permitir condição de pagamento livre" (§8.5). */
    @Input() public isCondicaoPagamentoLivre: boolean = true;

    @Input() public ehOrcamento: boolean = true;
    @Input() public dataEmissao: Date | string | null = null;
    @Input() public isSomenteLeitura: boolean = false;

    /** DF10: NSU e observação da parcela obrigatórios quando o contas a receber exige (só no pedido). */
    @Input() public isNsuObrigatorio: boolean = false;
    @Input() public isObservacaoParcelaObrigatoria: boolean = false;

    @Input({ required: true }) public mascaraDinheiro!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;

    @Output() public excluir = new EventEmitter<void>();

    /** Estado de UI do Pix/Boleto. Nasce do dado gravado no `ngOnInit`. */
    public meio: Meio = 'banco';

    /** O parcelamento escolhido no select da operadora — vira `condicaoPagamentoLivre` ("3x") ao gerar. */
    public readonly operadoraEscolhida = new FormControl<string | null>(null);

    /**
     * A forma que estava escolhida ANTES da troca. O aviso "Forma alterada" (§8.0, "trocar a forma
     * limpa…") só faz sentido quando havia uma — na primeira escolha não há nada sendo limpo.
     */
    private idFormaAnterior: number | null = null;

    constructor(
        private fb: FormBuilder,
        private alertService: AlertService,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['financas']) {
            this.atualizarListasDoAgregado();
            this.atualizarListasDependentes();
        }
    }

    public ngOnInit(): void {
        this.preSelecionarFavorita();

        this.idFormaAnterior = this.recebimento.get('idFormaRecebimento')?.value ?? null;
        this.meio = this.recebimento.get('idIntermediadorPagamento')?.value ? 'intermediador' : 'banco';

        // Condição da operadora gravada como texto ("3x", decisão D10) volta para o select.
        const livre = String(this.recebimento.get('condicaoPagamentoLivre')?.value ?? '');

        if (this.perfil.condicaoDaOperadora && /^\d+x$/.test(livre)) this.operadoraEscolhida.setValue(livre, { emitEvent: false });

        if (this.isSomenteLeitura) this.operadoraEscolhida.disable({ emitEvent: false });

        this.atualizarListasDependentes();
        this.sincronizarObrigatoriedades();
    }

    /**
     * O que é obrigatório num recebimento depende do PERFIL da forma e do meio escolhido (§8.3), e
     * muda quando a forma ou o meio trocam. O `Validators.required` vive no campo da vez, para o
     * asterisco e o destaque virem do próprio controle (nunca escrito à mão no rótulo). As regras
     * espelham `venda-pagamento.validacao.ts`, que continua sendo a palavra final ao salvar.
     */
    private sincronizarObrigatoriedades(): void {
        const perfil = this.perfil;
        const meioIntermediador = perfil.intermediacao === 'cartao'
            || (perfil.intermediacao === 'meio' && this.meio === 'intermediador');
        const contaObrigatoria = perfil.contaBancaria
            || (perfil.intermediacao === 'meio' && this.meio === 'banco')
            || perfil.contaDeRecebimento === 'contaCaixa'
            || perfil.contaDeRecebimento === 'obrigatoria';

        this.definirObrigatorio(this.recebimento.get('idFormaRecebimento'), true);
        this.definirObrigatorio(this.recebimento.get('idIntermediadorPagamento'), !!this.forma && meioIntermediador);
        this.definirObrigatorio(this.recebimento.get('idContaFinanceira'), !!this.forma && contaObrigatoria);

        // Condição: só onde o perfil a pede, e em UM dos três campos (operadora, texto livre, cadastro).
        const daOperadora = perfil.condicao && perfil.condicaoDaOperadora;
        const livre = this.usaCondicaoLivre;
        const doCadastro = perfil.condicao && !perfil.condicaoDaOperadora && !livre;

        this.definirObrigatorio(this.operadoraEscolhida, daOperadora);
        this.definirObrigatorio(this.recebimento.get('condicaoPagamentoLivre'), livre);
        this.definirObrigatorio(this.recebimento.get('idCondicaoPagamento'), doCadastro);
    }

    private definirObrigatorio(controle: AbstractControl | null, obrigatorio: boolean): void {
        if (!controle) return;

        if (obrigatorio) controle.addValidators(Validators.required);
        else controle.removeValidators(Validators.required);

        controle.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Recebimento NOVO nasce com a forma FAVORITA da empresa (§8.1; Pedro, 23/09: "vc usaria o
     * isFavorito, o padrao e para um seedDefault"). So deveria haver uma favorita — havendo mais,
     * vale a primeira. Passa pelo mesmo caminho da troca manual, para herdar intermediador, conta
     * e categoria preferenciais da forma. Recebimento gravado (id > 0) ou ja com forma nao e tocado.
     */
    private preSelecionarFavorita(): void {
        const ehNovo = Number(this.recebimento.get('idVendaPagamento')?.value ?? 0) === 0;

        if (!ehNovo || this.isSomenteLeitura || this.recebimento.get('idFormaRecebimento')?.value != null) return;

        const favorita = this.formas.find((f) => f.isFavorito);

        if (!favorita) return;

        this.recebimento.get('idFormaRecebimento')?.setValue(favorita.id, { emitEvent: false });
        this.aoTrocarForma();
    }

    // ==================================================================== a forma e o perfil

    /**
     * Os controles que os `select-pesquisavel` recebem. O componente exige `FormControl`; o
     * `get()` devolve `AbstractControl | null`, e o cast fica aqui — nunca no template.
     */
    public get formaControl(): FormControl { return this.recebimento.get('idFormaRecebimento') as FormControl; }
    public get categoriaControl(): FormControl { return this.recebimento.get('idCategoria') as FormControl; }
    public get intermediadorControl(): FormControl { return this.recebimento.get('idIntermediadorPagamento') as FormControl; }
    public get bandeiraControl(): FormControl { return this.recebimento.get('idIntermediadorBandeira') as FormControl; }
    public get contaControl(): FormControl { return this.recebimento.get('idContaFinanceira') as FormControl; }
    public get condicaoControl(): FormControl { return this.recebimento.get('idCondicaoPagamento') as FormControl; }

    // ================================================================ listas dos selects
    //
    // CAMPOS, e não getters: todas vão em `[offlineList]`, e o `select-pesquisavel` reprocessa a
    // cada referência nova — um `.filter()`/`?? []` em getter nascia novo a cada ciclo de detecção
    // (a armadilha documentada em vd-pagamento). As do agregado mudam só quando `financas` chega;
    // bandeiras e condições, quando a forma ou o intermediador trocam.

    public formas: FormaRecebimentoListaStruct[] = [];
    public categorias: LookupItemStruct[] = [];
    public intermediadores: IntermediadorPagamentoListaStruct[] = [];

    /**
     * Os intermediadores que o contas a receber aceita para a forma: os habilitados nela (a conta do
     * intermediador traz as formas ativas, `idsFormasRecebimentoIntermediadorAtivo`). Sem o vínculo o
     * Finanças recusa a conta do intermediador no lançamento.
     */
    public intermediadoresDaForma: IntermediadorPagamentoListaStruct[] = [];
    public bandeiras: IntermediadorBandeiraSelectStruct[] = [];

    /** Contas bancárias, para Depósito e Pix/Boleto via banco. */
    public contasBancarias: ContaFinanceira[] = [];

    /** Contas caixa, para a forma cadastrada com destino Caixa (§8.3). */
    public contasCaixa: ContaFinanceira[] = [];

    /**
     * As mesmas contas que a tela do contas a receber oferece para a forma (DF3). Campos, como as demais
     * listas: recalculados quando a forma ou o agregado mudam.
     */
    public contasDaForma: ContaFinanceira[] = [];

    /** Contas bancárias permitidas para a forma (boleto via banco só vê as vinculadas à forma). */
    public contasBancariasDaForma: ContaFinanceira[] = [];

    /**
     * Condições CADASTRADAS aplicáveis à forma escolhida (§8.5, modo select). Sem vínculo
     * cadastrado a condição não vale para a forma — é assim que o Finanças define "aplicável".
     */
    public condicoesAplicaveis: CondicaoPagamento[] = [];

    private atualizarListasDoAgregado(): void {
        const contas = this.financas?.listaContaFinanceira ?? [];

        this.formas = this.financas?.listaFormaRecebimento ?? [];
        this.categorias = this.financas?.listaCategoria ?? [];
        this.intermediadores = (this.financas?.listaIntermediadorPagamento ?? []).filter((i) => i.isAtivo);
        this.contasBancarias = contas.filter((c) => c.idTipoConta === TipoContaEnum.ContaBancaria);
        this.contasCaixa = contas.filter((c) => c.idTipoConta === TipoContaEnum.ContaCaixa);
    }

    private atualizarListasDependentes(): void {
        this.bandeiras = this.calcularBandeiras();
        this.contasDaForma = this.calcularContasDaForma();
        this.contasBancariasDaForma = this.contasDaForma.filter((c) => c.idTipoConta === TipoContaEnum.ContaBancaria);

        const idForma = this.forma?.id;
        const contas = this.financas?.listaContaFinanceira ?? [];
        this.intermediadoresDaForma = idForma == null ? [] : this.intermediadores.filter((i) => {
            const conta = contas.find((c) => Number(c.idContaFinanceira) === Number(i.idContaFinanceira));
            return (conta?.idsFormasRecebimentoIntermediadorAtivo ?? []).includes(idForma);
        });

        this.condicoesAplicaveis = (this.financas?.listaCondicaoPagamento ?? []).filter(
            (c) => c.isAtivo && (c.formasAplicaveis ?? []).some((f) => f.idFormaRecebimento === idForma),
        );
    }

    public get forma(): FormaRecebimentoListaStruct | null {
        const id = this.recebimento.get('idFormaRecebimento')?.value;

        return this.formas.find((f) => f.id === id) ?? null;
    }

    public get perfil(): PerfilDaForma {
        return perfilDaForma(this.forma, this.meio === 'intermediador');
    }

    public get contador(): string {
        return `${this.indice + 1}/${this.total}`;
    }

    public get intermediador(): IntermediadorPagamentoListaStruct | null {
        const id = this.recebimento.get('idIntermediadorPagamento')?.value;

        return this.intermediadores.find((i) => i.idIntermediadorPagamento === id) ?? null;
    }

    /**
     * Bandeiras do intermediador ESCOLHIDO — intermediador antes de bandeira (§8.3).
     *
     * Duas fontes, unidas: as "Bandeiras atendidas" do cadastro e as bandeiras que tem CONDICAO
     * COMERCIAL para a forma deste recebimento. A segunda e a que decide o parcelamento — e em dev
     * o cadastro tinha condicoes por bandeira com "atendidas" vazio (Pedro, 23/09: opcao 1).
     */
    private calcularBandeiras(): IntermediadorBandeiraSelectStruct[] {
        const intermediador = this.intermediador;

        if (!intermediador) return [];

        const idForma = this.forma?.id;
        const porId = new Map<number, IntermediadorBandeiraSelectStruct>();

        for (const b of intermediador.bandeiras ?? []) porId.set(b.idBandeira, b);

        for (const c of intermediador.condicoesComerciais ?? []) {
            if (c.idFormaRecebimento !== idForma || c.idBandeira == null || porId.has(c.idBandeira)) continue;

            porId.set(c.idBandeira, {
                idBandeira: c.idBandeira,
                descricao: c.descricaoBandeira ?? `Bandeira ${c.idBandeira}`,
                isPadrao: false,
            });
        }

        return [...porId.values()].sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
    }

    public get temContaBancaria(): boolean {
        return this.contasBancariasDaForma.length > 0;
    }

    /**
     * Espelho de `contas-receber-parcelamento.listaContaFinanceiraFiltrada`: dinheiro, crediário e vale-troca →
     * caixa; depósito e pix → bancária; boleto e cartão → bancária VINCULADA à forma na configuração ou conta de
     * recebimento com a forma ativa no intermediador; demais formas → todas, como no contas a receber.
     */
    private calcularContasDaForma(): ContaFinanceira[] {
        const todas = this.financas?.listaContaFinanceira ?? [];
        const forma = this.forma;
        if (!forma) return todas;

        const codigo = Number(forma.codigoPagamentoSefaz);
        const isValeTroca = (forma.descricao ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/-/g, ' ').trim().toLowerCase() === 'vale troca';
        const vinculadas = forma.contasBancariasVinculadas ?? [];
        const doIntermediador = (c: ContaFinanceira) => c.idTipoConta === TipoContaEnum.ContaRecebimento
            && (c.idsFormasRecebimentoIntermediadorAtivo ?? []).includes(forma.id);

        if (codigo === Number(CodigoSefazEnum.Dinheiro) || codigo === Number(CodigoSefazEnum.Crediario) || isValeTroca)
            return todas.filter((c) => c.idTipoConta === TipoContaEnum.ContaCaixa);
        if (codigo === Number(CodigoSefazEnum.Deposito) || codigo === Number(CodigoSefazEnum.Pix))
            return todas.filter((c) => c.idTipoConta === TipoContaEnum.ContaBancaria);
        if (codigo === Number(CodigoSefazEnum.Boleto) || codigo === Number(CodigoSefazEnum.CartaoCredito) || codigo === Number(CodigoSefazEnum.CartaoDebito))
            return todas.filter((c) => vinculadas.includes(Number(c.idContaFinanceira)) || doIntermediador(c));
        return todas;
    }

    public get hintContaBancaria(): string {
        if (this.temContaBancaria) return '';
        return this.contasBancarias.length > 0
            ? 'Nenhuma conta bancária vinculada a esta forma de recebimento. Vincule na configuração da forma.'
            : 'Nenhuma conta cadastrada.';
    }

    /**
     * DF10 por recebimento: NSU e observação só são exigidos na parcela do recebimento que vira título no
     * Finanças; o que vira movimentação de caixa ou banco não leva parcela (mesma regra do salvar).
     */
    public get viraTitulo(): boolean {
        return !recebimentoViraMovimentacao(
            this.recebimento,
            this.forma,
            this.financas?.listaContaFinanceira ?? [],
            this.financas?.listaIntermediadorPagamento ?? [],
        ).viraMovimentacao;
    }

    /**
     * O parcelamento que a OPERADORA oferece para esta forma (e bandeira): "1x"…"Nx" até
     * `parceladoAte`, com a taxa da condição comercial (§8.5, exceção; §8.6, origem da taxa).
     */
    public get opcoesDaOperadora(): OpcaoDaOperadora[] {
        const idForma = this.forma?.id;
        const idBandeira = this.recebimento.get('idIntermediadorBandeira')?.value ?? null;
        const maximo = maximoDeParcelas(this.intermediador, idForma, idBandeira);
        const opcoes: OpcaoDaOperadora[] = [];

        // A taxa é a da faixa que cobre N parcelas (1x a 2%, 6x a 3%...), a mesma que o lançamento aplica.
        for (let n = 1; n <= maximo; n++) {
            const cond = resolverCondicao(this.intermediador, idForma, idBandeira, n);

            opcoes.push({ id: `${n}x`, descricao: n === 1 ? '1x (à vista)' : `${n}x`, parcelas: n, taxa: cond?.taxa ?? null });
        }

        return opcoes;
    }

    /** Condição comercial do intermediador para ESTA forma, bandeira e nº de parcelas (a faixa que cobre o parcelamento). */
    private get condicaoComercialVigente(): IntermediadorCondicaoComercialSelectStruct | null {
        return resolverCondicao(this.intermediador, this.forma?.id, this.recebimento.get('idIntermediadorBandeira')?.value ?? null, this.quantidadeDeParcelas);
    }

    /** Parcelas do recebimento: as da grade, ou a escolha do select da operadora, ou 1 (à vista). */
    private get quantidadeDeParcelas(): number {
        if (this.parcelas.length) return this.parcelas.length;

        const escolha = this.opcoesDaOperadoraEscolhida;

        return escolha ? escolha.parcelas : 1;
    }

    private get opcoesDaOperadoraEscolhida(): { parcelas: number } | null {
        const id = this.operadoraEscolhida.value;
        const n = id ? Number(String(id).replace(/x$/, '')) : NaN;

        return isNaN(n) || n < 1 ? null : { parcelas: n };
    }

    // ==================================================================== conta de recebimento (§8.3)

    /** O texto da conta em LEITURA, quando ela é do intermediador ou do banco escolhido. */
    public get contaEmLeitura(): string | null {
        const p = this.perfil;

        if (p.contaDeRecebimento === 'leituraIntermediador') {
            const i = this.intermediador;

            return i
                ? `Conta do intermediador — ${i.descricaoContaFinanceira ?? i.descricao}`
                : 'Selecione o intermediador de pagamento';
        }

        if (p.contaDeRecebimento === 'leituraBanco') {
            const id = this.recebimento.get('idContaFinanceira')?.value;
            const c = this.contasBancariasDaForma.find((x) => x.idContaFinanceira === id);

            return c ? c.descricao : 'Definida pela conta bancária informada acima';
        }

        return null;
    }

    /** A conta que o RECEBIMENTO grava (`idContaFinanceira`), resolvida pela natureza (§8.3). */
    public get contaResolvida(): number | null {
        const p = this.perfil;

        if (p.contaDeRecebimento === 'leituraIntermediador') return this.intermediador?.idContaFinanceira ?? null;

        return this.recebimento.get('idContaFinanceira')?.value ?? null;
    }

    // ==================================================================== nota e simulação

    public get nota(): string | null {
        if (!this.forma) return null;

        // Sem conta bancária cadastrada, a orientação completa (§8.4) substitui a nota curta.
        if ((this.perfil.contaBancaria || (this.perfil.intermediacao === 'meio' && this.meio === 'banco')) && !this.temContaBancaria) {
            return 'Nenhuma conta bancária cadastrada. Cadastre uma em Finanças ▸ Contas bancárias para receber por esta forma.';
        }

        return notaDoMecanismo(this.perfil, this.condicaoComercialVigente?.taxa ?? null);
    }

    /**
     * Prévia do lançamento (T07), só exibição, com a mesma conta do orquestrador: pelo intermediador, vencimento pelo
     * prazo, taxa e tarifa por parcela e a tarifa lançada separado; pela condição cadastrada, os juros por parcela.
     */
    public get simulacao(): SimulacaoDoRecebimento | null {
        const valor = this.valor;

        if (valor <= 0) return null;

        const emissao = this.comoData(this.dataEmissao) ?? new Date();
        // Parcelas como o lançamento vai usar: as da grade, ou a escolha da operadora dividida em centavos, ou 1 à vista.
        const base = this.parcelas.length
            ? this.parcelas.controls.map((c, i) => ({ numeroParcela: Number(c.get('numeroParcela')?.value) || i + 1, valor: Number(c.get('valor')?.value) || 0 }))
            : gerarParcelas(valor, new Array(this.quantidadeDeParcelas).fill(0), emissao).map((p) => ({ numeroParcela: p.numeroParcela, valor: p.valor }));

        if (this.perfil.condicaoDaOperadora || (this.perfil.intermediacao === 'cartao' && !this.perfil.condicao)
            || (this.perfil.intermediacao === 'meio' && this.meio === 'intermediador')) {
            const intermediador = this.intermediador;
            const cond = this.condicaoComercialVigente;

            if (!intermediador || !cond || (this.perfil.condicaoDaOperadora && !this.operadoraEscolhida.value)) return null;

            const tarifa = UtilService.arredondarAbnt(cond.tarifa ?? 0, 2);
            const rateada = tarifa > 0 && intermediador.idTipoTarifacao === TipoTarifacaoEnum.DiaDoPagamento;
            const parcelas = calcularParcelasIntermediador(base, emissao, cond.prazo, cond.taxa ?? 0, rateada ? tarifa : 0, intermediador.considerarProximoDiaUtil);
            const dataTarifa = tarifa > 0 ? dataTarifaSeparada(intermediador.idTipoTarifacao, intermediador.diaDebito, new Date(), intermediador.considerarProximoDiaUtil) : null;

            return {
                daOperadora: true,
                taxa: cond.taxa ?? 0,
                nomeCondicao: null,
                parcelas,
                totalTaxa: UtilService.arredondarAbnt(parcelas.reduce((s, p) => s + p.taxa, 0), 2),
                totalTarifa: UtilService.arredondarAbnt(parcelas.reduce((s, p) => s + p.tarifa, 0), 2),
                totalLiquido: UtilService.arredondarAbnt(parcelas.reduce((s, p) => s + p.liquido, 0), 2),
                tarifaSeparada: dataTarifa ? { valor: tarifa, data: dataTarifa, conta: intermediador.descricaoContaFinanceira ?? intermediador.descricao } : null,
                totalJuros: 0,
                totalComJuros: valor,
            };
        }

        const condicao = this.condicaoEscolhida;

        if (!condicao || !condicao.taxaJuros) return null;

        // Juros por parcela, como o lançamento grava em `valor_juros_previsto`; o pedido não muda.
        const totalJuros = UtilService.arredondarAbnt(base.reduce((s, p) => s + UtilService.arredondarAbnt(p.valor * condicao.taxaJuros / 100, 2), 0), 2);

        return {
            daOperadora: false,
            taxa: condicao.taxaJuros,
            nomeCondicao: condicao.nome,
            parcelas: [],
            totalTaxa: 0,
            totalTarifa: 0,
            totalLiquido: valor,
            tarifaSeparada: null,
            totalJuros,
            totalComJuros: UtilService.arredondarAbnt(valor + totalJuros, 2),
        };
    }

    public get condicaoEscolhida(): CondicaoPagamento | null {
        const id = this.recebimento.get('idCondicaoPagamento')?.value;

        return this.condicoesAplicaveis.find((c) => c.idCondicaoPagamento === id) ?? null;
    }

    public get valor(): number {
        return Number(this.recebimento.get('valor')?.value) || 0;
    }

    public get parcelas(): FormArray {
        return this.recebimento.get('parcelas') as FormArray;
    }

    public get rotuloGerar(): string {
        return this.parcelas.length ? 'Atualizar parcelas' : 'Gerar parcelas';
    }

    /** Condição pelo texto livre (§8.5) — só fora da exceção da operadora e com a configuração ligada. */
    public get usaCondicaoLivre(): boolean {
        return this.perfil.condicao && !this.perfil.condicaoDaOperadora && this.isCondicaoPagamentoLivre;
    }

    // ==================================================================== reações

    /**
     * Trocar a forma limpa condição, parcelas, bandeira, meio, conta e intermediador, repondo o
     * intermediador e a conta PREFERENCIAIS da nova forma (§8.3) — e avisa.
     */
    public aoTrocarForma(): void {
        const forma = this.forma;
        const intermediadorPreferencial = this.intermediadorPreferencialDe(forma);

        this.recebimento.patchValue(
            {
                idCondicaoPagamento: null,
                condicaoPagamentoLivre: null,
                idIntermediadorBandeira: null,
                idIntermediadorPagamento: intermediadorPreferencial,
                idContaFinanceira: this.contaPreferencialDe(forma),
                idCategoria: forma?.idCategoriaFinanceira ?? null,
            },
            { emitEvent: false },
        );
        this.parcelas.clear({ emitEvent: false });
        this.operadoraEscolhida.setValue(null, { emitEvent: false });

        // O meio segue o preferencial: com intermediador preferencial, nasce em Intermediador.
        this.meio = intermediadorPreferencial ? 'intermediador' : 'banco';

        // Só avisa quando TROCOU: na primeira escolha não havia condição nem parcelas a limpar.
        if (forma && this.idFormaAnterior != null && this.idFormaAnterior !== forma.id) {
            this.alertService.show(
                'Forma alterada',
                `Condição, parcelas e campos dependentes foram limpos para ${forma.descricao}.`,
                AlertType.info,
            );
        }

        this.idFormaAnterior = forma?.id ?? null;
        this.atualizarListasDependentes();
        this.limparIntermediadorInvalido();
        this.limparContaInvalida();
        this.sincronizarObrigatoriedades();
    }

    public get hintIntermediador(): string {
        return this.intermediadoresDaForma.length > 0
            ? ''
            : 'Nenhum intermediador habilitado para esta forma. Habilite na configuração da forma de recebimento.';
    }

    /** O intermediador que não está habilitado na nova forma sai do recebimento, com a bandeira. */
    private limparIntermediadorInvalido(): void {
        const id = this.recebimento.get('idIntermediadorPagamento')?.value ?? null;
        if (id == null) return;

        if (!this.intermediadoresDaForma.some((i) => i.idIntermediadorPagamento === id)) {
            this.recebimento.patchValue({ idIntermediadorPagamento: null, idIntermediadorBandeira: null }, { emitEvent: false });
            this.atualizarListasDependentes();
        }
    }

    /** Como no contas a receber: a conta que não vale para a nova forma sai do recebimento. */
    private limparContaInvalida(): void {
        const id = this.recebimento.get('idContaFinanceira')?.value ?? null;
        if (id == null) return;

        if (!this.contasDaForma.some((c) => Number(c.idContaFinanceira) === Number(id)))
            this.recebimento.get('idContaFinanceira')?.setValue(null, { emitEvent: false });
    }

    /** Trocar intermediador, bandeira ou meio limpa condição e parcelas (§8.3). */
    public aoTrocarDependente(): void {
        this.recebimento.patchValue({ idCondicaoPagamento: null, condicaoPagamentoLivre: null }, { emitEvent: false });
        this.parcelas.clear({ emitEvent: false });
        this.operadoraEscolhida.setValue(null, { emitEvent: false });
        this.atualizarListasDependentes();
        this.sincronizarObrigatoriedades();
    }

    public aoTrocarMeio(meio: Meio): void {
        if (meio === this.meio) return;

        this.meio = meio;

        // Banco não tem intermediador; Intermediador não tem conta bancária escolhida.
        this.recebimento.patchValue(
            meio === 'banco'
                ? { idIntermediadorPagamento: null, idIntermediadorBandeira: null }
                : { idContaFinanceira: null, idIntermediadorPagamento: this.intermediadorPreferencialDe(this.forma) },
            { emitEvent: false },
        );
        this.aoTrocarDependente();
    }

    /** No modo select, escolher a condição gera as parcelas na hora (§8.5). */
    public aoEscolherCondicao(): void {
        this.gerarParcelas(false);
    }

    /**
     * Gerar/Atualizar parcelas (§8.5): os dias vêm do texto livre, da condição cadastrada ou do
     * parcelamento da operadora. Nenhuma taxa entra nas parcelas (D-B): a soma delas é o valor
     * do recebimento, que é o que a folha confere; a taxa fica na simulação da §8.6.
     */
    public gerarParcelas(avisarSeVazio: boolean = true): void {
        if (this.valor <= 0) {
            if (avisarSeVazio) this.alertService.show('Atenção', 'Informe o valor do recebimento antes de gerar as parcelas.', AlertType.warning);

            return;
        }

        let dias: number[] = [];

        if (this.perfil.condicaoDaOperadora) {
            const escolha = this.opcoesDaOperadora.find((o) => o.id === this.operadoraEscolhida.value);

            if (!escolha) {
                if (avisarSeVazio) this.alertService.show('Atenção', 'Selecione o parcelamento oferecido pelo intermediador.', AlertType.warning);

                return;
            }

            // T07: as parcelas vencem pelo prazo da condição comercial (i × prazo), com o próximo dia útil quando o
            // intermediador pede — a mesma conta do lançamento.
            const cond = resolverCondicao(this.intermediador, this.forma?.id, this.recebimento.get('idIntermediadorBandeira')?.value ?? null, escolha.parcelas);
            const emissaoOperadora = this.comoData(this.dataEmissao) ?? new Date();

            dias = diasDoParcelamentoDoIntermediador(escolha.parcelas, cond?.prazo ?? 30, emissaoOperadora, !!this.intermediador?.considerarProximoDiaUtil);
            // D10: a condição da operadora não tem id em condicao_pagamento — vai como texto.
            this.recebimento.patchValue({ condicaoPagamentoLivre: escolha.id, idCondicaoPagamento: null }, { emitEvent: false });
        } else if (this.usaCondicaoLivre) {
            dias = interpretarCondicaoLivre(this.recebimento.get('condicaoPagamentoLivre')?.value);

            if (!dias.length) {
                if (avisarSeVazio) {
                    this.alertService.show('Condição inválida', 'Informe prazos em dias, como "30 60", "3x" ou "15 +2x".', AlertType.warning);
                }

                return;
            }
        } else {
            const cond = this.condicaoEscolhida;

            if (!cond) {
                if (avisarSeVazio) this.alertService.show('Atenção', 'Selecione uma condição de pagamento cadastrada.', AlertType.warning);

                return;
            }

            dias = diasDaCondicao(cond);
        }

        const emissao = this.comoData(this.dataEmissao) ?? new Date();
        const calculadas = gerarParcelas(this.valor, dias, emissao);

        this.parcelas.clear({ emitEvent: false });

        for (const p of calculadas) {
            const linha = montarFormParcela(this.fb);

            linha.patchValue(
                { numeroParcela: p.numeroParcela, dias: p.dias, dataVencimento: p.dataVencimento, valor: p.valor },
                { emitEvent: false },
            );
            this.parcelas.push(linha, { emitEvent: false });
        }

        // A conta que a parcela vai herdar (§8.3) fica gravada no recebimento.
        if (this.perfil.contaDeRecebimento === 'leituraIntermediador') {
            this.recebimento.get('idContaFinanceira')?.setValue(this.contaResolvida, { emitEvent: false });
        }
    }

    /**
     * O preferencial do cadastro só vale se ainda estiver ATIVO: o agregado traz só contas e
     * intermediadores ativos, e um id fora da lista deixaria o select vazio com um valor gravado
     * por baixo — em dev a conta preferencial do Boleto (24) está exatamente nessa situação.
     */
    private intermediadorPreferencialDe(forma: FormaRecebimentoListaStruct | null): number | null {
        const id = forma?.idIntermediadorPreferencial ?? null;

        return id != null && this.intermediadores.some((i) => i.idIntermediadorPagamento === id) ? id : null;
    }

    private contaPreferencialDe(forma: FormaRecebimentoListaStruct | null): number | null {
        const id = forma?.idContaFinanceiraPreferencial ?? null;

        return id != null && (this.financas?.listaContaFinanceira ?? []).some((c) => c.idContaFinanceira === id) ? id : null;
    }

    private comoData(valor: unknown): Date | null {
        if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
        if (typeof valor === 'string' && valor) {
            const d = new Date(valor);

            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    }
}
